import QRCode from 'qrcode';

/**
 * Visuel de parrainage (1080 × 1350, format portrait des réseaux sociaux) dessiné dans
 * un canvas, avec le code du parrain et un QR code vers son lien.
 *
 * Ordre de préférence du fond :
 *  1. /parrainage/visuel-generique.jpg — illustration SANS nom de concours : le concours
 *     choisi par le parrain (« Concours CRPE : ») est écrit dans l'emplacement réservé
 *     (GENERIC.slot), ou rien s'il n'en choisit pas ;
 *  2. /parrainage/visuel-dgfip.jpg — illustration « Concours DGFiP » (titre incrusté),
 *     seulement quand le parrain vise la DGFiP ;
 *  3. sinon, un gabarit dessiné ici sur le même message (se tester plutôt que relire —
 *     MonQuiz-book, ch. 3 et 4).
 * Sur une illustration, un bandeau compact remplace seulement son bouton final
 * « Entraîne-toi sur MonQuiz.fr » (hauteur propre à chaque illustration).
 * Jamais « Objectif DGFiP » (marque tierce).
 */
const W = 1080, H = 1350, BAND = 300;
// Calage des illustrations (coordonnées dans le canvas 1080 × 1350).
// Illustration générique « révise plus efficacement » (titre en minuscule, fait pour suivre
// « Concours X : ») : emplacement au-dessus du titre, sur le fond uni ; bandeau à la place
// du bouton et de « monquiz.fr », sous le bloc jaune.
const GENERIC = { src: '/parrainage/visuel-generique.jpg', band: 215, slot: { x: 55, baseline: 170, maxW: 575, size: 80 } };
const DGFIP = { src: '/parrainage/visuel-dgfip.jpg', band: 155 };
const DISPLAY = "'Plus Jakarta Sans', system-ui, sans-serif";
const BODY = "'DM Sans', system-ui, sans-serif";

const STEPS = [
    ['1', 'Réponds à des QCM', 'Des questions corrigées et expliquées, sujet par sujet.'],
    ['2', 'Analyse tes erreurs', 'Chaque réponse fausse te dit pourquoi, tout de suite.'],
    ['3', 'Reviens au bon moment', 'Les notions fragiles reviennent quand tu allais les oublier.'],
];

function loadImage(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

// Retour à la ligne simple, mot à mot, dans une largeur donnée.
function wrap(ctx, text, x, y, maxW, lineH) {
    const words = text.split(' ');
    let line = '';
    for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > maxW && line) {
            ctx.fillText(line, x, y);
            line = w; y += lineH;
        } else line = test;
    }
    if (line) ctx.fillText(line, x, y);
    return y + lineH;
}

function drawTemplate(ctx, kicker) {
    const t = { kicker, line1: 'pourquoi relire', line2: 'ne suffit pas ?' };
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, '#1e1b4b');
    g.addColorStop(0.55, '#312e81');
    g.addColorStop(1, '#4c1d95');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    // Halo décoratif.
    const halo = ctx.createRadialGradient(W - 140, 180, 20, W - 140, 180, 420);
    halo.addColorStop(0, 'rgba(167,139,250,0.35)');
    halo.addColorStop(1, 'rgba(167,139,250,0)');
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, W, H);

    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#c4b5fd';
    ctx.font = `700 34px ${DISPLAY}`;
    ctx.fillText('monquiz.fr', 72, 104);

    ctx.fillStyle = '#ffffff';
    fitText(ctx, `${t.kicker} :`, 72, 230, W - 144, 82);
    ctx.font = `800 82px ${DISPLAY}`;
    ctx.fillStyle = '#fde047';
    ctx.fillText(t.line1, 72, 322);
    ctx.fillText(t.line2, 72, 414);

    ctx.fillStyle = '#e0e7ff';
    ctx.font = `500 38px ${BODY}`;
    ctx.fillText('Teste ta mémoire plutôt que de relire.', 72, 490);

    // Trois étapes.
    const cardW = (W - 72 * 2 - 24 * 2) / 3, cardY = 548, cardH = 300;
    STEPS.forEach(([n, title, text], i) => {
        const x = 72 + i * (cardW + 24);
        ctx.fillStyle = 'rgba(255,255,255,0.09)';
        roundRect(ctx, x, cardY, cardW, cardH, 26);
        ctx.fill();
        ctx.fillStyle = '#22d3ee';
        ctx.beginPath(); ctx.arc(x + 46, cardY + 52, 26, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#0f172a';
        ctx.font = `800 30px ${DISPLAY}`;
        ctx.textAlign = 'center';
        ctx.fillText(n, x + 46, cardY + 63);
        ctx.textAlign = 'left';
        ctx.fillStyle = '#ffffff';
        ctx.font = `800 30px ${DISPLAY}`;
        const after = wrap(ctx, title, x + 24, cardY + 124, cardW - 44, 36);
        ctx.fillStyle = '#c7d2fe';
        ctx.font = `500 24px ${BODY}`;
        wrap(ctx, text, x + 24, after + 8, cardW - 44, 31);
    });

    // Bandeau régularité.
    ctx.fillStyle = 'rgba(253,224,71,0.14)';
    roundRect(ctx, 72, 880, W - 144, 120, 24);
    ctx.fill();
    ctx.fillStyle = '#fde047';
    ctx.font = `800 40px ${DISPLAY}`;
    ctx.fillText('La régularité fait la différence.', 104, 932);
    ctx.fillStyle = '#fef9c3';
    ctx.font = `500 28px ${BODY}`;
    ctx.fillText('Quelques minutes chaque jour suffisent pour progresser.', 104, 976);
}

// Texte en 800 réduit jusqu'à tenir dans maxW (un nom de concours peut être long).
function fitText(ctx, text, x, baseline, maxW, size) {
    let s = size;
    ctx.font = `800 ${s}px ${DISPLAY}`;
    while (s > 34 && ctx.measureText(text).width > maxW) {
        s -= 2;
        ctx.font = `800 ${s}px ${DISPLAY}`;
    }
    ctx.textAlign = 'left';
    ctx.fillText(text, x, baseline);
}

// Bandeau compact posé sur une illustration : code + QR, à la place de son bouton final.
async function drawCompactBand(ctx, code, link, height) {
    const y = H - height;
    const dy = (height - 155) / 2; // contenu centré dans le bandeau
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, y, W, height);
    ctx.fillStyle = '#7c3aed';
    ctx.fillRect(0, y, W, 8);
    const qrSize = Math.min(124 + dy, 170);
    const qr = await QRCode.toDataURL(link, { width: Math.round(qrSize), margin: 1, color: { dark: '#1e1b4b', light: '#ffffff' } });
    const qrImg = await loadImage(qr);
    if (qrImg) ctx.drawImage(qrImg, W - 56 - qrSize, y + (height - qrSize) / 2 + 4, qrSize, qrSize);
    ctx.textAlign = 'left';
    ctx.fillStyle = '#6d28d9';
    ctx.font = `700 26px ${DISPLAY}`;
    ctx.fillText('Mon code de parrainage · 7 jours offerts', 56, y + dy + 50);
    ctx.fillStyle = '#1e1b4b';
    ctx.font = `800 62px ${DISPLAY}`;
    ctx.fillText(code, 56, y + dy + 116);
    ctx.fillStyle = '#64748b';
    ctx.font = `600 24px ${BODY}`;
    ctx.fillText('monquiz.fr', 56, y + dy + 146);
}

function drawCover(ctx, img) {
    const scale = Math.max(W / img.width, H / img.height);
    const dw = img.width * scale, dh = img.height * scale;
    ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
}

async function drawBand(ctx, code, link) {
    const y = H - BAND;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, y, W, BAND);
    ctx.fillStyle = '#7c3aed';
    ctx.fillRect(0, y, W, 10);

    const qrSize = 220;
    const qr = await QRCode.toDataURL(link, { width: qrSize, margin: 1, color: { dark: '#1e1b4b', light: '#ffffff' } });
    const qrImg = await loadImage(qr);
    if (qrImg) ctx.drawImage(qrImg, W - 72 - qrSize, y + (BAND - qrSize) / 2 + 4, qrSize, qrSize);

    ctx.textAlign = 'left';
    ctx.fillStyle = '#6d28d9';
    ctx.font = `700 30px ${DISPLAY}`;
    ctx.fillText('Mon code de parrainage', 72, y + 78);
    ctx.fillStyle = '#1e1b4b';
    ctx.font = `800 76px ${DISPLAY}`;
    ctx.fillText(code, 72, y + 160);
    ctx.fillStyle = '#334155';
    ctx.font = `600 28px ${BODY}`;
    ctx.fillText('7 jours offerts sur ton abonnement', 72, y + 212);
    ctx.fillStyle = '#64748b';
    ctx.font = `500 24px ${BODY}`;
    ctx.fillText('Entraîne-toi sur monquiz.fr', 72, y + 254);
}

/**
 * Dessine le visuel dans `canvas` (redimensionné à 1080 × 1350).
 * heading  : titre de l'emplacement (« Concours CRPE : », « ISTQB — CTFL Foundation : ») ou null.
 * dgfip    : le concours choisi relève de la DGFiP (autorise l'illustration DGFiP).
 */
export async function renderReferralVisual(canvas, { code, link, heading = null, dgfip = false }) {
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    try { await document.fonts?.ready; } catch { /* polices système à défaut */ }

    const generic = await loadImage(GENERIC.src);
    if (generic) {
        drawCover(ctx, generic);
        // Sans concours choisi, « Pour ton concours : » garde la phrase correcte.
        ctx.fillStyle = '#ffffff';
        const { x, baseline, maxW, size } = GENERIC.slot;
        fitText(ctx, heading || 'Pour ton concours :', x, baseline, maxW, size);
        await drawCompactBand(ctx, code, link, GENERIC.band);
        return;
    }
    const dgfipImg = dgfip ? await loadImage(DGFIP.src) : null;
    if (dgfipImg) {
        drawCover(ctx, dgfipImg);
        await drawCompactBand(ctx, code, link, DGFIP.band);
        return;
    }
    drawTemplate(ctx, heading ? heading.replace(/\s*:$/, '') : 'Concours');
    await drawBand(ctx, code, link);
}

export function canvasToFile(canvas, name) {
    return new Promise((resolve) => canvas.toBlob(b => resolve(b ? new File([b], name, { type: 'image/png' }) : null), 'image/png'));
}
